// src/components/admin/DocumentViewerModal.jsx
import React, { useState, useEffect } from 'react';
import '../../styles/components/admin/DoctorVerificationDetails.css';

const DocumentViewerModal = ({ isOpen, onClose, document: documentData }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && documentData) {
      setLoading(true);
      setError(null);
    }
  }, [isOpen, documentData]);

  if (!isOpen || !documentData) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get file extension from URL
  const getFileExtension = (url) => {
    if (!url) return '';
    const parts = url.split('.');
    return parts[parts.length - 1].toLowerCase();
  };

  // Get friendly document type
  const getDocumentType = (url) => {
    const extension = getFileExtension(url);
    if (['pdf'].includes(extension)) return 'PDF';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return 'Image';
    if (['doc', 'docx'].includes(extension)) return 'Word Document';
    if (['xls', 'xlsx'].includes(extension)) return 'Excel Spreadsheet';
    return 'Document';
  };

  // Extract filename from URL
  const extractFilename = (url) => {
    if (!url) return 'Document';
    try {
      const fullFilename = url.split('/').pop();
      const parts = fullFilename.split('_');
      if (parts.length > 1) {
        return parts[parts.length - 1];
      }
      return fullFilename;
    } catch (err) {
      return 'Document';
    }
  };

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setError('Failed to load document. Please try downloading it instead.');
  };

  // Document information
  const docType = getDocumentType(documentData.url);
  const fileName = documentData.title || extractFilename(documentData.url);
  
  // Determine if the document is a PDF
  const isPdf = docType === 'PDF';
  
  return (
    <div className="dvm-overlay" onClick={handleOverlayClick} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div className="dvm-modal" style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        width: '100%',
        height: '90vh',
        maxWidth: '1300px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Modal Header */}
        <div className="dvm-header" style={{
          padding: '16px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div className="dvm-title-section">
            <h2 className="dvm-title" style={{margin: 0, fontSize: '18px'}}>{fileName}</h2>
            <span className="dvm-type-badge" style={{
              backgroundColor: '#e9f1fd',
              color: '#4a90e2',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              marginLeft: '8px'
            }}>{docType}</span>
          </div>
          <div className="dvm-actions">
            <button
              className="dvm-close-btn"
              onClick={onClose}
              title="Close Modal"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="dvm-content" style={{ 
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          height: 'calc(90vh - 100px)' 
        }}>
          {loading && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'white',
              zIndex: 2
            }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
              <p>Loading document...</p>
            </div>
          )}
          
          {isPdf && !error ? (
            <iframe
              src={documentData.url}
              style={{ 
                width: '100%', 
                height: '100%', 
                border: 'none',
                display: 'block'
              }}
              onLoad={handleLoad}
              onError={handleError}
              title={fileName}
            />
          ) : (
            !error && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>📄</div>
                <p>This document type cannot be previewed. Please use the button below to open it.</p>
                <button
                  onClick={() => window.open(documentData.url, '_blank')}
                  style={{ 
                    padding: '12px 24px', 
                    backgroundColor: '#4a90e2', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginTop: '20px'
                  }}
                >
                  Open Document
                </button>
              </div>
            )
          )}
          
          {error && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '30px',
              height: '100%'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>📄</div>
              <p style={{ 
                fontSize: '16px',
                marginBottom: '25px', 
                textAlign: 'center', 
                color: '#555',
                maxWidth: '80%'
              }}>
                {error}
              </p>
              <button 
                onClick={() => window.open(documentData.url, '_blank')}
                style={{ 
                  padding: '12px 24px', 
                  backgroundColor: '#4a90e2', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
                </svg>
                Open Document
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewerModal;